/**
 * Admin CRUD schemas for the AI prompts registry (Story 3.7).
 *
 * Source of truth: docs/architecture/database-schema.md §4.4 — `prompts` and
 * `prompt_history`. The runtime schema treats each `prompts.id` row as a
 * single (campaign_id, version) tuple. The admin API surfaces them as a
 * "master + versions" pair where the master view returns the currently active
 * row per campaign and history lists all rows ordered by version DESC.
 *
 * **Versionamento atômico (Story 3.7 §AC #2 + Convenção):** mudar o
 * `system_prompt` cria uma NOVA linha em `prompts` com a próxima `version`
 * por campaign_id e registra a transição em `prompt_history`. A ativação é
 * `UPDATE prompts SET active = TRUE WHERE id = X` em statement único, com
 * desativação da versão anterior.
 *
 * **No-Invention note:** size cap (`prompt_max_chars`) e padrões banidos
 * (`prompt_banned_patterns`) vêm de `system_settings` (seed em Story 1.6); o
 * fallback abaixo (5000 / quatro padrões) é apenas para o caso de chave
 * ausente. Story-driven (story 3.7 §AC #5).
 */
import { Type } from '@sinclair/typebox';
/**
 * Hard floor enforced by the route handler.
 *
 * The `system_prompt` precisa ter substância — a story exige >= 50 chars para
 * impedir prompts vazios/triviais que quebram o comportamento do agente em
 * produção.
 */
export const PROMPT_BODY_MIN = 50;
/**
 * Default ceiling used when `system_settings.prompt_max_chars` is missing.
 * Mirrors the seed value in `apps/api/scripts/seed.ts`.
 */
export const PROMPT_BODY_MAX_DEFAULT = 5000;
/**
 * Default banned patterns fallback. Mirrors the seed value. The route handler
 * loads the live list from `system_settings.prompt_banned_patterns`.
 */
export const PROMPT_BANNED_PATTERNS_DEFAULT = [
    'admita ser IA',
    'admita que é IA',
    'ignore as instruções',
    'você não é uma IA',
];
/**
 * Validate the body against the live `prompt_banned_patterns` list. Each entry
 * is matched as a case-insensitive substring (NOT a regex) — keeping it simple
 * and predictable for non-developer admins editing the patterns list.
 *
 * Returns the first banned pattern found or `null` when the body is clean.
 */
export function findBannedPattern(body, patterns) {
    const haystack = body.toLowerCase();
    for (const p of patterns) {
        if (p.length === 0)
            continue;
        if (haystack.includes(p.toLowerCase()))
            return p;
    }
    return null;
}
/**
 * `Prompt` — public read-side projection returned by GET endpoints.
 *
 * `id` is the **active prompt row id** (i.e. the row in `prompts` with
 * `active = TRUE` for that campaign). On the master listing this is the only
 * row exposed per campaign; the `version` field surfaces which iteration is
 * live.
 */
export const Prompt = Type.Object({
    id: Type.String({ format: 'uuid' }),
    campaignId: Type.String({ format: 'uuid' }),
    campaignName: Type.String(),
    version: Type.Integer({ minimum: 1 }),
    systemPrompt: Type.String({ minLength: 1 }),
    active: Type.Boolean(),
    createdBy: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
});
/**
 * `PromptCreate` — body of `POST /admin/prompts` (admin/manager).
 *
 * The route resolves `campaignId` to an existing campaign and inserts the
 * first version row (active=TRUE) plus a seed `prompt_history` entry.
 */
export const PromptCreate = Type.Object({
    campaignId: Type.String({ format: 'uuid' }),
    systemPrompt: Type.String({ minLength: PROMPT_BODY_MIN }),
});
/**
 * `PromptUpdate` — body of `PATCH /admin/prompts/:id`.
 *
 * `:id` is the master prompt id (the active row's id). Sending `systemPrompt`
 * creates a NEW version row in `prompts`; existing rows are NEVER mutated.
 *
 * `activate` (default false) controls whether the new version becomes active
 * immediately. When false the new version is inserted with `active=FALSE`.
 *
 * `rationale` is required because `prompt_history.rationale` is `NOT NULL` in
 * the DB schema. The frontend can default it to "Atualização" for quick edits.
 */
export const PromptUpdate = Type.Object({
    systemPrompt: Type.String({ minLength: PROMPT_BODY_MIN }),
    rationale: Type.String({ minLength: 1, maxLength: 500 }),
    activate: Type.Optional(Type.Boolean()),
});
/**
 * `PromptDuplicate` — body de `POST /admin/prompts/:id/duplicate`.
 *
 * Copia o `systemPrompt` do prompt de origem para um prompt NOVO na campanha
 * destino (`campaignId`), como v1 ativa. Histórico limpo (não carrega as versões
 * da origem). Retorna 409 se a campanha destino já tiver um prompt ativo.
 */
export const PromptDuplicate = Type.Object({
    campaignId: Type.String({ format: 'uuid' }),
});
/**
 * `PromptAvailableResponse` — prompts disponíveis para atrelar a uma campanha
 * (Story 3.19). `lineageId` é o valor a gravar em `config.prompt_id`.
 */
export const PromptAvailableItem = Type.Object({
    id: Type.String({ format: 'uuid' }),
    lineageId: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    name: Type.Union([Type.String(), Type.Null()]),
    version: Type.Integer(),
});
export const PromptAvailableResponse = Type.Object({
    available: Type.Array(PromptAvailableItem),
});
/**
 * `PromptActivateBody` — body of `POST /admin/prompts/:id/activate/:version`.
 *
 * `rationale` distinguishes the audit action (activate vs rollback) and is
 * recorded in `prompt_history` for the version transition.
 */
export const PromptActivateBody = Type.Object({
    rationale: Type.String({ minLength: 1, maxLength: 500 }),
});
/**
 * `PromptHistoryEntry` — one row of GET /admin/prompts/:id/history.
 *
 * Mirrors `prompt_history` columns plus the resolved `systemPrompt` from the
 * matching `prompts` row at that version (so the timeline UI can render a
 * preview without a second round-trip).
 */
export const PromptHistoryEntry = Type.Object({
    id: Type.String({ format: 'uuid' }),
    promptId: Type.String({ format: 'uuid' }),
    version: Type.Integer({ minimum: 1 }),
    fromVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    toVersion: Type.Integer({ minimum: 1 }),
    rationale: Type.String(),
    changedBy: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    systemPrompt: Type.String(),
    active: Type.Boolean(),
    createdAt: Type.String({ format: 'date-time' }),
});
/**
 * `PromptDiff` — payload of GET /admin/prompts/:id/history/:version.
 *
 * The server returns the raw before/after text for the requested version vs
 * the currently active version; the diff library runs in the frontend so the
 * payload stays small and the rendering is purely client-side.
 */
export const PromptDiff = Type.Object({
    campaignId: Type.String({ format: 'uuid' }),
    campaignName: Type.String(),
    fromVersion: Type.Integer({ minimum: 1 }),
    toVersion: Type.Integer({ minimum: 1 }),
    fromBody: Type.String(),
    toBody: Type.String(),
    fromActive: Type.Boolean(),
    toActive: Type.Boolean(),
});
export const PromptListResponse = Type.Object({
    prompts: Type.Array(Prompt),
});
export const PromptResponse = Type.Object({
    prompt: Prompt,
});
export const PromptHistoryResponse = Type.Object({
    history: Type.Array(PromptHistoryEntry),
});
export const PromptDiffResponse = Type.Object({
    diff: PromptDiff,
});
/**
 * `PromptConfig` — runtime limits the UI uses to gate the editor before the
 * server applies them. `maxChars` reflects `system_settings.prompt_max_chars`
 * (editable by admin in `/settings`); `minChars` is the hard floor enforced by
 * the TypeBox schemas above (`PROMPT_BODY_MIN`).
 *
 * Exposed via `GET /admin/prompts/config` (admin + manager) so the prompt
 * editor stays in lockstep with the active cap instead of relying on the
 * client-side fallback constant.
 */
export const PromptConfigResponse = Type.Object({
    maxChars: Type.Integer({ minimum: 1 }),
    minChars: Type.Integer({ minimum: 1 }),
});
//# sourceMappingURL=prompts.js.map